import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Footer, MarketingHeader, SEO } from '@/components';
import { CheckCard, CheckHistogram } from '@/components/check';
import { API_BASE_URL } from '@/lib/api';
import { isCheckSlug, type CheckCard as CheckCardData, type CheckSlug } from '@/lib/check/quizzes';
import { track } from '@/lib/analytics';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

export const Route = createFileRoute('/check/$quiz/r/$publicId')({
  beforeLoad: ({ params }) => {
    if (!isCheckSlug(params.quiz)) {
      throw redirect({ to: '/check' });
    }
  },
  component: CheckResultPage,
});

function CheckResultPage() {
  const { quiz: quizSlug, publicId } = Route.useParams();
  const slug = quizSlug as CheckSlug;
  const [card, setCard] = useState<CheckCardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState('Share your result');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/checks/${publicId}`);
        if (!res.ok) throw new Error('This result is gone.');
        const data = (await res.json()) as { card: CheckCardData };
        if (!cancelled) setCard(data.card);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load this result.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  useEffect(() => {
    if (!card) return;
    track('Check Result View', {
      quiz: slug,
      public_id: publicId,
      type: card.type_key,
    });
  }, [card, publicId, slug]);

  const ogImage = useMemo(
    () => `${API_BASE_URL || 'https://harborcoparent.com'}/v1/checks/${publicId}/og.png`,
    [publicId],
  );
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/check/${slug}/r/${publicId}`
      : `https://harborcoparent.com/check/${slug}/r/${publicId}`;

  const onShare = async () => {
    track('Check Share Tapped', { quiz: slug, public_id: publicId });
    const title = card?.og_title || 'Harbor check';
    const text = card?.identity_line || 'See where you sit.';
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel('Link copied');
      window.setTimeout(() => setShareLabel('Share your result'), 2500);
    } catch {
      setShareLabel('Copy the link from the address bar');
    }
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      }
    } catch {
      // share sheet canceled or unavailable
    }
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      <SEO
        title={card?.og_title || 'Harbor check'}
        description={card?.og_description || 'See where you sit next to other parents.'}
        path={`/check/${slug}/r/${publicId}`}
        ogImage={ogImage.startsWith('http') ? ogImage : `https://harborcoparent.com${ogImage}`}
        noindex
      />
      <MarketingHeader />
      <div className='mx-auto max-w-lg px-6 pb-16 pt-32'>
        {error ? (
          <p className='text-center text-gray-600' style={nunito}>
            {error}{' '}
            <Link to='/check/$quiz' params={{ quiz: slug }} className='font-semibold text-primary'>
              Check yours
            </Link>
          </p>
        ) : null}
        {card ? (
          <>
            <CheckCard card={card} />
            <CheckHistogram card={card} />
            <div className='mt-8 grid gap-3'>
              <button
                type='button'
                onClick={() => void onShare()}
                className='rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white shadow-sm hover:bg-primary-hover'
                style={nunito}
              >
                {shareLabel}
              </button>
              <Link
                to='/check/$quiz'
                params={{ quiz: slug }}
                onClick={() => track('Check Yours Tapped', { quiz: slug })}
                className='rounded-2xl border border-primary px-6 py-4 text-center text-lg font-semibold text-primary'
                style={nunito}
              >
                Check yours
              </Link>
              <a
                href={card.cta_href}
                className='pt-2 text-center text-sm font-semibold text-gray-600 underline-offset-2 hover:underline'
                style={nunito}
              >
                {card.cta_label}
              </a>
            </div>
            <p className='mt-8 text-center text-xs text-gray-500' style={nunito}>
              Educational, not legal advice. No names stored.
            </p>
          </>
        ) : !error ? (
          <p className='text-center text-gray-500' style={nunito}>
            Loading your card…
          </p>
        ) : null}
      </div>
      <Footer />
    </div>
  );
}
